// @flow

import _ from 'underscore';
import type {IntegerType} from "./model";

type GlassInfoType = {
	/**
	 * Kas klaas on 90 kraadi keeratud
	 */
	rotated: boolean,

	/**
	 * Kas klaas on 180 kraadi keeratud
	 */
	keeratud: boolean,
	/**
	 * Kas klaas on peegeldatud
	 */
	peegeldatud: boolean,
	/**
	 * kujundit ümbritseva ristküliku x-laius
	 */
	tahvelx: IntegerType,
	/**
	 * kujundit ümbritseva ristküliky y-laius
	 */
	tahvely: IntegerType,
	/**
	 * kujundi maksimaalne x koordinaat
	 */
	maxx: IntegerType,
	/**
	 * kujundi maksimaalne y koordinaat
	 */
	maxy: IntegerType,
	/**
	 * kujundi minimaalne x koordinaat
	 */
	minx: IntegerType,
	/**
	 * kujundi minimaalne y koordinaat
	 */
	miny: IntegerType,
	/**
	 * massiiv lõikude x1 koordinaatidest
	 */
	joonx1: Array<IntegerType>,
	/**
	 * massiiv lõikude x2 koordinaatidest
	 */
	joonx2: Array<IntegerType>,
	/**
	 * massiiv lõikude y1 koordinaatidest
	 */
	joony1: Array<IntegerType>,
	/**
	 * massiiv lõikude y2 koordinaatidest
	 */
	joony2: Array<IntegerType>,
	/**
	 * Ridade arv
	 */
	ridu: IntegerType,
	/**
	 * kujundi keskpunkti x koordinaat
	 */
	keskx: IntegerType,
	/**
	 * kujundi keskpunkti y koordinaat
	 */
	kesky: IntegerType
};

/**
 * Klass kajastab klaasi andmeid, mis on sisse parsitud NCP failist.
 * Kasutamine:
 *  var g = new GlassInfo(isRotated);
 *  g.parseNcp(data);
 *  
 *  Parsitud info paigutatakse:
 *  
 *  g.joonx1 - massiiv lõikude x1 koordinaatidest
 *  g.joonx2 - massiiv lõikude x2 koordinaatidest
 *  g.joony1 - massiiv lõikude y1 koordinaatidest
 *  g.joony2 - massiiv lõikude y2 koordinaatidest
 *
 *  Lisaks arvutatakse juurde:
 *
 *  g.tahvelx - kujundit ümbritseva ristküliku x-laius
 *  g.tahvely - kujundit ümbritseva ristküliky y-laius
 *  g.maxx - kujundi maksimaalne x koordinaat
 *  g.maxy - kujundi maksimaalne y koordinaat
 *  g.minx - kujundi minimaalne x koordinaat
 *  g.miny - kujundi minimaalne y koordinaat
 *  g.keskx - kujundi keskpunkti x koordinaat
 *  g.kesky - kujundi keskpunkti y koordinaat
 *
 */
class GlassInfo {
	/**
	 * Kas klaas on 90 kraadi keeratud
	 */
	rotated: boolean;

	/**
	 * Kas klaas on 180 kraadi keeratud
	 */
	keeratud: boolean;
	/**
	 * Kas klaas on peegeldatud
	 */
	peegeldatud: boolean;
	/**
	 * kujundit ümbritseva ristküliku x-laius
	 */
	tahvelx: IntegerType;
	/**
	 * kujundit ümbritseva ristküliky y-laius
	 */
	tahvely: IntegerType;
	/**
	 * kujundi maksimaalne x koordinaat
	 */
	maxx: IntegerType;
	/**
	 * kujundi maksimaalne y koordinaat
	 */
	maxy: IntegerType;
	/**
	 * kujundi minimaalne x koordinaat
	 */
	minx: IntegerType;
	/**
	 * kujundi minimaalne y koordinaat
	 */
	miny: IntegerType;
	/**
	 * massiiv lõikude x1 koordinaatidest
	 */
	joonx1: Array<IntegerType>;
	/**
	 * massiiv lõikude x2 koordinaatidest
	 */
	joonx2: Array<IntegerType>;
	/**
	 * massiiv lõikude y1 koordinaatidest
	 */
	joony1: Array<IntegerType>;
	/**
	 * massiiv lõikude y2 koordinaatidest
	 */
	joony2: Array<IntegerType>;
	/**
	 * Ridade arv
	 */
	ridu: IntegerType;
	/**
	 * kujundi keskpunkti x koordinaat
	 */
	keskx: IntegerType;
	/**
	 * kujundi keskpunkti y koordinaat
	 */
	kesky: IntegerType;

	/**
	 *
	 * @param data
	 */
	constructor(data?: ?GlassInfo | ?GlassInfoType) {
		this.rotated = false;
		this.keeratud = false;
		this.peegeldatud = false;
		this.tahvelx = 0;
		this.tahvely = 0;
		this.maxx = 0;
		this.maxy = 0;
		this.minx = 0;
		this.miny = 0;
		this.joonx1 = [];
		this.joonx2 = [];
		this.joony1 = [];
		this.joony2 = [];
		this.ridu = 0;
		this.keskx = 0;
		this.kesky = 0;

		if (data) this.extend(data);
	}
	
	extend(data: GlassInfo | GlassInfoType): void {
		for (let item of _.keys(data)) {
			if (!(item in this)) continue;
			
			if (Array.isArray(data[item])) {
				this[item] = data[item].slice();
			} else {
				this[item] = data[item];
			}
		}
	}
	
	/**
	 * loefail:
	 * 		kaaresta
	 * 		arvutakeskpunkt
	 * 		arvuta_e
	 * 			arvuta_p
	 * 			koordinaadid
	 * 		arvuta_p
	 * 		koordinaadid
	 * 			arvuta_Kalle
	 * 		joonista
	 */
    loadGlassData(data: GlassInfo | GlassInfoType, rotated: boolean): void {
        // Väärtustame:
        this.rotated = rotated;
        this.keeratud = false;
        this.peegeldatud = false;
        this.extend(data);

        this.calcShape();
    }
	
	calcShape(): void {
	    this.tahvelx = 2000;
	    this.tahvely = 2000;
	    this.maxx = 0;
	    this.maxy = 0;
	    this.minx = this.tahvelx;
	    this.miny = this.tahvely;

	    for (let i = 0; i < this.ridu; i++) {
	        if (this.joonx2[i] < this.minx) this.minx = this.joonx2[i];
	        if (this.joony2[i] < this.miny) this.miny = this.joony2[i];
	        if (this.joonx2[i] > this.maxx) this.maxx = this.joonx2[i];
	        if (this.joony2[i] > this.maxy) this.maxy = this.joony2[i];
	    }

	    for (let i = 0; i < this.ridu; i++) {
	        this.joonx1[i] += Math.abs(this.minx) + 10.111;
	        this.joonx2[i] += Math.abs(this.minx) + 10.111;
	        this.joony1[i] += Math.abs(this.miny) + 10.111;
	        this.joony2[i] += Math.abs(this.miny) + 10.111;
	    }
	    
	    this.maxx = this.maxx + Math.abs(this.minx) + 10;
	    this.maxy = this.maxy + Math.abs(this.miny) + 10;
	    this.minx = this.minx + Math.abs(this.minx) + 10;
	    this.miny = this.miny + Math.abs(this.miny) + 10;
	    this.tahvelx = this.maxx + 10;
	    this.tahvely = this.maxy + 10;
	    
	    this.arvutaKeskpunkt();
	}

	arvutaKeskpunkt(): void {
	    this.keskx = (this.maxx - this.minx) / 2 + this.minx;
	    this.kesky = (this.maxy - this.miny) / 2 + this.miny;
	    for (let i = 0; i < this.ridu; i++) {
	        this.joonx1[i] = this.joonx1[i] - this.keskx;
	        this.joonx2[i] = this.joonx2[i] - this.keskx;
	        this.joony1[i] = this.joony1[i] - this.kesky;
	        this.joony2[i] = this.joony2[i] - this.kesky;
	    }
	}

    rotate90(): void {
        for (let i = 0; i < this.ridu; i++) {
            let tmp = this.joonx2[i];
            this.joonx2[i] = this.joony2[i];
            this.joony2[i] = tmp;

            tmp = this.joonx1[i];
            this.joonx1[i] = this.joony1[i];
            this.joony1[i] = tmp;
        }
        this.rotated = !this.rotated;
        this.calcShape();
    }

    rotate180(): void {
		for (let i = 0; i < this.ridu; i++) {
			this.joonx1[i] = (this.joonx1[i] > 0 ? 0 - this.joonx1[i] : Math.abs(this.joonx1[i]));
			this.joonx2[i] = (this.joonx2[i] > 0 ? 0 - this.joonx2[i] : Math.abs(this.joonx2[i]));
			this.joony1[i] = (this.joony1[i] > 0 ? 0 - this.joony1[i] : Math.abs(this.joony1[i]));
			this.joony2[i] = (this.joony2[i] > 0 ? 0 - this.joony2[i] : Math.abs(this.joony2[i]));
		}
		this.keeratud = !this.keeratud;
	}
	
	mirror(): void {
	    let abimaxx = -10000;
	    let abiminx = 10000;
	    for (let i = 0; i < this.ridu; i++) {
	        if (this.joonx1[i] > abimaxx) abimaxx = this.joonx1[i];
	        if (this.joonx1[i] < abiminx) abiminx = this.joonx1[i];
	    }
	    for (let i = 0; i < this.ridu; i++) {
	        this.joonx1[i] = abimaxx - this.joonx1[i] + abiminx;
	        this.joonx2[i] = abimaxx - this.joonx2[i] + abiminx;
	    }
		this.peegeldatud = !this.peegeldatud;
	}
}

export default GlassInfo;
